import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Plus, Trash2, Send, Pencil } from "lucide-react";

export default function Alerts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", enabled: true });

  const load = async () => {
    try {
      const { data } = await api.get("/alerts/recipients");
      setRows(Array.isArray(data) ? data : []);
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/alerts/recipients/${editing.id}`, form);
        toast.success("Güncellendi");
      } else {
        await api.post("/alerts/recipients", form);
        toast.success("Eklendi");
      }
      setOpen(false); setEditing(null); setForm({ email: "", name: "", enabled: true });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sil?")) return;
    await api.delete(`/alerts/recipients/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const sendTest = async () => {
    try {
      const { data } = await api.post("/alerts/test");
      toast.success(`Test e-postası gönderildi (${data.recipients.length} alıcı)`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bildirimler</h1>
          <p className="text-sm text-muted-foreground mt-1">Sağlık / senkron hatalarında alert alacak e-postalar</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={sendTest} data-testid="alerts-send-test"><Send size={14} className="mr-1.5" />Test Gönder</Button>
          <Button onClick={() => { setEditing(null); setForm({ email: "", name: "", enabled: true }); setOpen(true); }} data-testid="alerts-add-btn">
            <Plus size={14} className="mr-1.5" />Alıcı Ekle
          </Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-md overflow-hidden">
        <table className="data-table" data-testid="alerts-table">
          <thead>
            <tr><th>E-posta</th><th>İsim</th><th>Durum</th><th className="text-right">İşlem</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-muted-foreground text-sm font-mono">alıcı yok</td></tr>}
            {rows.map((a) => (
              <tr key={a.id}>
                <td className="font-mono text-xs">{a.email}</td>
                <td>{a.name || "—"}</td>
                <td><span className={`status-badge ${a.enabled ? "ok" : "muted"}`}>{a.enabled ? "aktif" : "pasif"}</span></td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(a); setForm({ email: a.email, name: a.name, enabled: a.enabled }); setOpen(true); }}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(a.id)} className="text-red-400"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-border bg-card rounded-md p-4 text-sm">
        <h2 className="text-sm font-semibold mb-2">SMTP Yapılandırması</h2>
        <div className="text-xs text-muted-foreground">
          SMTP ayarları sunucu tarafında backend/.env dosyasında tanımlıdır (SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD).
          Değiştirmek için backend .env güncellenip backend servisi yeniden başlatılmalıdır.
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Alıcı Düzenle" : "Yeni Alıcı"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="alert-form-email" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">İsim (opsiyonel)</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
              <span className="text-sm">Aktif</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={save} data-testid="alert-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
